import { createBrowserClient, createServerClient } from "@supabase/ssr";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

let browserClient: SupabaseClient | null = null;
let searchClient: SupabaseClient | null = null;

export function getSupabaseBrowserClient(): SupabaseClient {
  if (!browserClient) {
    browserClient =
      typeof window === "undefined"
        ? createClient(supabaseUrl, supabaseKey)
        : createBrowserClient(supabaseUrl, supabaseKey);
  }

  return browserClient;
}

/**
 * Анонімний клієнт без роботи з cookies/сесією.
 * Використовуй для публічного пошуку (ігри, видавці), щоб запити не блокувались
 * оновленням токена при залогіненому користувачу.
 * Створюється лише на клієнті (typeof window !== "undefined").
 */
export function getSupabaseSearchClient(): SupabaseClient {
  if (typeof window === "undefined") {
    throw new Error("getSupabaseSearchClient() can only be called in the browser");
  }
  if (!searchClient) {
    searchClient = createClient(supabaseUrl, supabaseKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    });
  }
  return searchClient;
}

export type SupabaseCookieMethods = {
  getAll: () => { name: string; value: string }[];
  setAll: (
    cookies: {
      name: string;
      value: string;
      options?: Record<string, unknown>;
    }[],
  ) => void;
};

/**
 * Серверний Supabase-клієнт.
 *
 * - Якщо передати `cookieMethods`, буде створено SSR-клієнт (Auth через cookies).
 * - Якщо не передавати, повертаємо звичайний anon client (для публічних читань).
 *
 * Важливо: цей файл імпортується і в клієнтському коді (пошук), тому тут НЕ можна
 * статично імпортувати `next/headers`. Для Next.js cookies-інтеграції використовуй
 * server-only helper (див. `src/lib/api/supabase.next.ts`).
 */
export async function createSupabaseServerClient(
  cookieMethods?: SupabaseCookieMethods,
): Promise<SupabaseClient> {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error("Missing Supabase environment variables");
  }

  // ── Auth / user-scoped reads ──────────────────────────────────────────
  // Anon key + cookies → requests run as the logged-in user (RLS enforced).
  // Used by getServerAuth/getUserPlan (user_profiles), watchlist, etc.
  if (cookieMethods) {
    return createServerClient(url, anonKey, { cookies: cookieMethods });
  }

  // ── Public catalog reads (no session) ─────────────────────────────────
  // Uses the SERVICE_ROLE key, which is SERVER-ONLY (not NEXT_PUBLIC, never in
  // the client bundle). This is the anti-scraping boundary: anon/authenticated
  // SELECT/EXECUTE on catalog tables + RPCs are revoked at the DB, so a bot with
  // the public anon key (or a throwaway account) can read NOTHING directly via
  // PostgREST — all catalog data flows through this server path only.
  //
  // Catalog data is public by design, so bypassing RLS here changes nothing
  // (the old policy was `using true`). NEVER use this branch for user-scoped
  // reads — those must pass cookieMethods so RLS scopes to the owner.
  if (typeof window !== "undefined") {
    throw new Error(
      "createSupabaseServerClient() without cookies is server-only",
    );
  }

  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  return createClient(url, serviceKey || anonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });
}
